import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Input } from '../common/Form';
import { STATES } from '../../constants/StatesConstants';

const INPUT_OVERRIDE = `
  .location-modal-form input,
  .location-modal-form select {
    padding: 10px 12px !important;
    font-size: 0.8125rem !important;
    height: 40px !important;
    box-sizing: border-box !important;
  }
`;

export interface LocationInfoData {
  locationName: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website: string;
}

interface EditLocationInfoModalProps {
  isOpen: boolean;
  data?: LocationInfoData | null;
  onCancel: () => void;
  onSave: (data: LocationInfoData) => void;
}

const emptyData: LocationInfoData = {
  locationName: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: '',
  website: ''
};

const EditLocationInfoModal = ({ isOpen, data, onCancel, onSave }: EditLocationInfoModalProps) => {
  const [formData, setFormData] = useState<LocationInfoData>(emptyData);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData(emptyData);
    }
  }, [data]);

  if (!isOpen) {
    return null;
  }

  const updateField = (field: keyof LocationInfoData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Modal onOverlayClicked={onCancel}>
      <div className="location-modal-form">
        <style>{INPUT_OVERRIDE}</style>

        <div className="modal-header">
          <h1 className="modal-title">EDIT BASIC INFORMATION</h1>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <Input
            name="locationName"
            label="LOCATION NAME"
            value={formData.locationName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('locationName', e.target.value)}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '16px' }}>
            <Input
              name="streetAddress1"
              label="STREET ADDRESS 1"
              value={formData.streetAddress1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('streetAddress1', e.target.value)}
            />
            <Input
              name="streetAddress2"
              label="STREET ADDRESS 2"
              value={formData.streetAddress2}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('streetAddress2', e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '24px', marginTop: '16px' }}>
            <Input
              name="city"
              label="CITY"
              value={formData.city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('city', e.target.value)}
            />
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="font-semibold text-sm tracking-[1.1375px]" htmlFor="editState">
                STATE
              </label>
              <div className="mt-2">
                <select
                  id="editState"
                  name="state"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '0.8125rem',
                    borderRadius: '2px',
                    border: '1px solid #a3a3a3',
                    backgroundColor: 'white',
                    height: '40px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select</option>
                  {STATES.map((state: { label: string; value: string }) => (
                    <option value={state.value} key={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Input
              name="zip"
              label="ZIP"
              value={formData.zip}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('zip', e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '16px' }}>
            <Input
              name="phone"
              label="PHONE NUMBER"
              value={formData.phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('phone', e.target.value)}
            />
            <Input
              name="email"
              label="EMAIL ADDRESS"
              value={formData.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('email', e.target.value)}
            />
          </div>

          <div style={{ marginTop: '16px' }}>
            <Input
              name="website"
              label="WEBSITE"
              value={formData.website}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('website', e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px 20px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              width: '140px',
              height: '35px',
              backgroundColor: 'var(--color-danger)',
              color: 'white',
              borderRadius: '999px',
              border: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '1.4px',
              cursor: 'pointer'
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            // TODO: Wire up to API call to update location info
            onClick={() => onSave(formData)}
            style={{
              width: '140px',
              height: '35px',
              backgroundColor: 'var(--color-accent)',
              color: 'white',
              borderRadius: '999px',
              border: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '1.4px',
              cursor: 'pointer'
            }}
          >
            NEXT
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditLocationInfoModal;
